import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { isMsisdnValid, formatMsisdn } from '@/functions/isMsisdnValid'
import { isSubscribed, readSubscriptionStatus } from '@/functions/subscriptionStatus'
import { armEvina as loadEvinaKit } from '@/functions/evina'
import MadapiService from '@/services/madapiService'

const TRANSACTION_ID_STORAGE_KEY = 'wr-lp-transaction-id'

// Funnel steps, all on the landing route (Evina needs its protected button on the page it was
// armed on): plan -> otp (non-HE only) -> confirm. Thank-you and error are their own routes.
export const Steps = { Plan: 'plan', Otp: 'otp', Confirm: 'confirm' }

function createCorrelationId(selector) {
  return `wr-cm-${selector}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
}

export const useSubscriptionStore = defineStore('subscription', () => {
  const msisdn = ref('')
  const headerEnriched = ref(false)
  const step = ref(Steps.Plan)
  const selectedTier = ref(null)
  const otp = ref('')
  const otpSentAt = ref(0)
  const busy = ref(false)
  const alreadySubscribed = ref(false)
  const subscriptionStatus = ref('')
  // The message MTN / the service returned on a failed subscribe, shown on the failure page.
  const mnoError = ref('')
  // A translation key for a refusal with its own copy (evinaBlocked); empty for every other failure.
  const errorCode = ref('')
  // Evina kit state for the current attempt; ti is the transaction the service checks on /subscribe.
  // settled flips once the arm attempt finished either way: the confirm CTA waits for it.
  const evina = ref({ armed: false, gatewayLoaded: null, ti: null, settled: false })

  // Affiliate attribution from ?transaction_id; persisted so it survives reloads.
  const transactionId = ref(readStoredTransactionId())
  const clickId = ref(null)
  const campaignId = ref(null)
  const publisherId = ref(null)

  const productId = computed(() => selectedTier.value?.productId ?? null)
  const networkServiceId = computed(() => selectedTier.value?.networkServiceId ?? null)

  function readStoredTransactionId() {
    try {
      return localStorage.getItem(TRANSACTION_ID_STORAGE_KEY) || ''
    } catch {
      return ''
    }
  }

  function setCampaignParameters(params) {
    if (params?.transactionId) {
      transactionId.value = params.transactionId
      try {
        localStorage.setItem(TRANSACTION_ID_STORAGE_KEY, params.transactionId)
      } catch {
        // storage unavailable: the id still lives in memory for this page view
      }
    }
    clickId.value = params?.clickId ?? null
    campaignId.value = params?.campaignId ?? null
    publisherId.value = params?.publisherId ?? null
  }

  function selectTier(tier) {
    selectedTier.value = tier
  }

  // Pre-subscribe check. Fails OPEN: a MADAPI outage must not block new subscriptions.
  async function checkSubscription(brandConfig, number) {
    try {
      const user = await MadapiService.getUser({ msisdn: number, serviceId: brandConfig.serviceId })
      subscriptionStatus.value = readSubscriptionStatus(user)
      alreadySubscribed.value = isSubscribed(user)
    } catch (error) {
      console.error('[MADAPI] get-user failed', error)
      subscriptionStatus.value = ''
      alreadySubscribed.value = false
    }
    return alreadySubscribed.value
  }

  // Plan step CTA: the visitor's first click. It arms the Evina kit (so Evina measures the whole
  // attempt from here: OTP entry through the Confirm tap) and, for a typed number, sends the PIN.
  // HE visitors skip the OTP (MTN identified the SIM). The kit loads alongside the OTP request, never
  // ahead of it: a slow Evina must not delay the SMS.
  async function continueWithNumber(brandConfig) {
    if (busy.value) return { ok: false }
    busy.value = true
    try {
      const number = formatMsisdn(msisdn.value, brandConfig.msisdn)
      if (!headerEnriched.value && !isMsisdnValid(number, brandConfig.msisdn)) return { ok: false, reason: 'invalidNumber' }
      msisdn.value = number

      if (await checkSubscription(brandConfig, number)) return { ok: false, reason: 'alreadySubscribed' }

      armEvina(brandConfig)

      if (headerEnriched.value) {
        step.value = Steps.Confirm
        return { ok: true }
      }

      const sent = await requestOtp(brandConfig)
      if (!sent.ok) return sent
      step.value = Steps.Otp
      return { ok: true }
    } finally {
      busy.value = false
    }
  }

  async function requestOtp(brandConfig) {
    try {
      const result = await MadapiService.requestOtp({
        msisdn: msisdn.value,
        serviceId: brandConfig.serviceId,
        productId: productId.value ?? brandConfig.productId
      })
      if (!result?.otpSent) return { ok: false, reason: 'otpSendError', result }
      otpSentAt.value = Date.now()
      otp.value = ''
      return { ok: true }
    } catch (error) {
      console.error('[MADAPI] otp request failed', error)
      return { ok: false, reason: 'otpSendError', error }
    }
  }

  async function verifyOtp(brandConfig, code) {
    if (busy.value) return { ok: false }
    busy.value = true
    try {
      const result = await MadapiService.verifyOtp({
        msisdn: msisdn.value,
        serviceId: brandConfig.serviceId,
        productId: productId.value ?? brandConfig.productId,
        otp: code
      })
      if (!result?.valid) return { ok: false, reason: 'otpError' }
      step.value = Steps.Confirm
      return { ok: true }
    } catch (error) {
      console.error('[MADAPI] otp verify failed', error)
      return { ok: false, reason: 'otpError', error }
    } finally {
      busy.value = false
    }
  }

  // Evina kit, one transaction per (number, tier): issued at the first click, re-bound to
  // #subscribe_button when the confirm step renders. A kit that fails to load never blocks the
  // attempt: the service decides what an unverified subscribe does (fail open).
  async function armEvina(brandConfig) {
    try {
      const result = await loadEvinaKit({
        msisdn: msisdn.value,
        serviceId: brandConfig.serviceId,
        productId: productId.value ?? brandConfig.productId,
        transactionId: transactionId.value || undefined
      })
      evina.value = {
        armed: Boolean(result.ok && !result.skipped),
        gatewayLoaded: result.gatewayLoaded ?? evina.value.gatewayLoaded,
        ti: result.ti ?? evina.value.ti,
        settled: true
      }
      if (!result.ok) console.warn('[Evina] kit not armed', result.reason)
    } catch (error) {
      evina.value = { ...evina.value, settled: true }
      console.warn('[Evina] kit not armed', error)
    }
  }

  // Confirm step: the Evina-protected tap. The service checks the Evina transaction, queues the
  // attribution, calls MTN Subscription v2 and answers with MTN's response; the activation itself
  // arrives later on the Comviva callback.
  async function subscribe(brandConfig) {
    if (busy.value) return { ok: false }
    busy.value = true
    mnoError.value = ''
    errorCode.value = ''
    try {
      const plan = networkServiceId.value || brandConfig.networkServiceId
      await MadapiService.subscribe({
        msisdn: msisdn.value,
        serviceId: brandConfig.serviceId,
        productId: productId.value ?? brandConfig.productId,
        networkServiceId: plan,
        registrationChannel: brandConfig.registrationChannel || 'WEB',
        transactionId: transactionId.value || undefined,
        correlationId: transactionId.value || createCorrelationId(plan),
        evinaTransactionId: evina.value.ti || undefined
      })
      return { ok: true }
    } catch (error) {
      console.error('[MADAPI] subscribe failed', error)
      if (MadapiService.isEvinaBlocked(error)) {
        errorCode.value = 'evinaBlocked'
        return { ok: false, reason: 'evinaBlocked', error }
      }
      mnoError.value = MadapiService.errorMessage(error)
      return { ok: false, reason: 'subscribeError', error }
    } finally {
      busy.value = false
    }
  }

  function changeNumber() {
    otp.value = ''
    alreadySubscribed.value = false
    if (!headerEnriched.value) msisdn.value = ''
    // A new number is a new Evina transaction; the next first click arms it again.
    evina.value = { armed: false, gatewayLoaded: null, ti: null, settled: false }
    step.value = Steps.Plan
  }

  // Portal hand-off carries the msisdn so the portal can auto-log the subscriber in.
  function portalHref(brandConfig) {
    const base = (brandConfig.portalUrl || '').replace(/\/$/, '')
    if (!base) return ''
    const number = formatMsisdn(msisdn.value, brandConfig.msisdn)
    return number ? `${base}/?msisdn=${encodeURIComponent(number)}` : `${base}/`
  }

  return {
    msisdn,
    headerEnriched,
    step,
    selectedTier,
    productId,
    networkServiceId,
    otp,
    otpSentAt,
    busy,
    alreadySubscribed,
    subscriptionStatus,
    mnoError,
    errorCode,
    evina,
    transactionId,
    clickId,
    campaignId,
    publisherId,
    setCampaignParameters,
    selectTier,
    checkSubscription,
    continueWithNumber,
    requestOtp,
    verifyOtp,
    armEvina,
    subscribe,
    changeNumber,
    portalHref
  }
})
